import { api } from '../api';
import type { EquipoVenta } from '../catalogs/equipos-venta';

export type EstadoVenta =
  | 'vendido'
  | 'ya_ms2'
  | 'acepto_en_pago'
  | 'oferta_enviada'
  | 'perdido'
  | 'en_gestion'
  | 'interesado'
  | 'no_opto'
  | 'sin_gestion';

/**
 * Canal DERIVADO de la oferta (no es `ofertaTmk.origen` crudo: SkyTel nunca
 * escribe 'skytel' ahí). Lo clasifica el backend en `canal-oferta.catalog.ts`.
 */
export type CanalVenta = 'skytel' | 'IB' | 'OB' | 'medicus' | 'agente';

/** Quién envió/gestiona la oferta. Lo deriva el backend en `agente-venta.ts`. */
export interface AgenteVenta {
  tipo: 'persona' | 'chatbot' | 'skytel' | 'medicus';
  nombre: string;
  email?: string;
}

export interface VentaItem {
  legajoId: string;
  numero: string;
  titular: { apellido: string; nombres: string; documento: string };
  estadoVentaMs2: EstadoVenta;
  canal: CanalVenta | null;
  agente: AgenteVenta | null;
  /** Equipo de ventas del agente (VEN-516). `null` = sin agente o agente sin equipo. */
  equipo: EquipoVenta | null;
  planActual: 'MS1' | 'MS2' | 'otro' | null;
  ultimaGestionAt: string | null;
  fechaVenta: string | null;
  /** Delta MS2−base sin IVA: cobrado, o estimado si `montoEstimado`. */
  monto: number | null;
  /** `true` = precio de lista vigente para el grupo, no plata cobrada acá. */
  montoEstimado: boolean;
  /** Integrantes vigentes del grupo (los que se cotizan/cobran). */
  capitas: number;
  /** Venta cerrada por fuera del sitio, registrada a mano (sin pago en el micrositio). */
  esVentaManual: boolean;
}

export interface VentasResp {
  items: VentaItem[];
  total: number;
  page: number;
  pageSize: number;
  porEstado: Record<string, number>;
  porCanal: Record<string, number>;
  porEquipo: Record<string, number>;
}

export interface VentasCatalogos {
  estados: { key: EstadoVenta; label: string }[];
  canales: { key: CanalVenta; label: string }[];
  equipos: { key: EquipoVenta; label: string }[];
}

export interface VentasQuery {
  estados?: EstadoVenta[];
  canales?: CanalVenta[];
  equipos?: EquipoVenta[];
  q?: string;
  page?: number;
  pageSize?: number;
  sort?: 'ultimaGestion' | 'fechaVenta' | 'monto';
  dir?: 'asc' | 'desc';
  /** Rango sobre "Fecha venta" (`YYYY-MM-DD`), ambos límites inclusive. */
  fechaDesde?: string;
  fechaHasta?: string;
}

export async function getVentasCatalogos(): Promise<VentasCatalogos> {
  const { data } = await api.get<VentasCatalogos>('/ventas-comisiones/catalogos');
  return data;
}

export async function listVentas(query: VentasQuery): Promise<VentasResp> {
  const { data } = await api.get<VentasResp>('/ventas-comisiones', {
    params: {
      ...(query.estados?.length ? { estados: query.estados.join(',') } : {}),
      ...(query.canales?.length ? { canales: query.canales.join(',') } : {}),
      ...(query.equipos?.length ? { equipos: query.equipos.join(',') } : {}),
      ...(query.q?.trim() ? { q: query.q.trim() } : {}),
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 25,
      ...(query.sort ? { sort: query.sort } : {}),
      ...(query.dir ? { dir: query.dir } : {}),
      ...(query.fechaDesde ? { fechaDesde: query.fechaDesde } : {}),
      ...(query.fechaHasta ? { fechaHasta: query.fechaHasta } : {}),
    },
  });
  return data;
}
